import pkceChallenge from 'react-native-pkce-challenge';
import AuthService, { GetTokenResponse } from '../../services/AuthService';
import { jwtDecode } from 'jwt-decode';
import { AsyncStorage, SecureStorage } from '@bonanza-social/toolkit/storage';
import {
  persistReducer,
  createAsyncThunk,
  createSlice,
  PayloadAction,
  REHYDRATE,
  injectSlice,
} from '@bonanza-social/toolkit/state';
import {
  AuthState,
  GetTokenPayload,
  LoginPayload,
  LoginRejectedPayload,
  AuthStateRehydration,
  ResendCodePayload,
  ResendCodeResult,
  ValidateMFAPayload,
} from './auth.slice.types';
import { ApiError, Token } from '@bonanza-social/toolkit/api';
import { clientId } from './auth.constants';

export const getToken = async (data: GetTokenPayload): Promise<Token> => {
  const token = await AuthService.getToken({
    data: {
      client_id: clientId,
      ...data,
    },
  });

  const parsedToken = jwtDecode(token.id_token);

  if (!parsedToken.sub) {
    throw new Error('Missing sub');
  }

  await SecureStorage.setItem('token', JSON.stringify(token));

  await SecureStorage.setItem('access_token', token.access_token);
  await SecureStorage.setItem('user_id', parsedToken.sub);
  await SecureStorage.setItem('refresh_token', token.refresh_token);

  return token;
};

let refreshTokenPromise: Promise<Token> | undefined;

export const refreshToken = createAsyncThunk<
  Token,
  undefined,
  { rejectValue: { status: ApiError['status']; data: ApiError['data'] } }
>('auth/refreshToken', async (_, { rejectWithValue }) => {
  try {
    const refreshToken = await SecureStorage.getItem('refresh_token');

    if (!refreshTokenPromise) {
      refreshTokenPromise = getToken({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
      });
    }

    const token = await refreshTokenPromise;

    refreshTokenPromise = undefined;

    return token;
  } catch (e) {
    refreshTokenPromise = undefined;

    if (e instanceof ApiError) {
      return rejectWithValue({ status: e.status, data: e.data });
    }

    throw e;
  }
});

export const login = createAsyncThunk<
  GetTokenResponse,
  LoginPayload,
  { rejectValue: LoginRejectedPayload | ApiError }
>(
  'auth/login',
  async (
    {
      email,
      password,
      acceptTc,
      isEligible = false,
      googleRecaptchaToken,
    }: LoginPayload,
    { rejectWithValue },
  ) => {
    try {
      const { codeChallenge, codeVerifier } = pkceChallenge();
      const data = {
        email,
        password,
        acceptTc,
        googleRecaptchaToken,
        clientId: clientId,
        scope: 'openid profile bonanzasocial-api offline_access',
        redirectUri: 'bonanzasocial://callback',
        codeChallenge,
      };

      const {
        authorizationCode,
        requireTwoFactorVerification,
        token: mfaToken,
      } = await AuthService.login({
        data,
        headers: {
          'X-Fit-To-Play': isEligible,
        },
      });

      if (requireTwoFactorVerification) {
        return rejectWithValue({
          email,
          token: mfaToken,
          codeChallenge,
          codeVerifier,
          requireTwoFactorVerification,
        });
      }

      const token = await getToken({
        grant_type: 'authorization_code',
        redirect_uri: 'bonanzasocial://callback',
        code: authorizationCode,
        code_verifier: codeVerifier,
      });

      return token;
    } catch (e) {
      if (e instanceof ApiError) {
        return rejectWithValue(e);
      }

      throw e;
    }
  },
);

export const validateMFA = createAsyncThunk<
  GetTokenResponse,
  ValidateMFAPayload,
  { rejectValue: ApiError }
>(
  'auth/validateMFA',
  async (
    { email, code, token: mfaToken, isEligible, codeChallenge, codeVerifier },
    { rejectWithValue },
  ) => {
    try {
      const data = {
        email,
        token: mfaToken,
        clientId: clientId,
        scope: 'openid profile bonanzasocial-api offline_access',
        redirectUri: 'bonanzasocial://callback',
        codeChallenge,
        verificationCode: code,
      };
      const { authorizationCode } = await AuthService.validateMFA({
        data,
        headers: {
          'X-Fit-To-Play': isEligible,
        },
      });

      const token = await getToken({
        grant_type: 'authorization_code',
        redirect_uri: 'bonanzasocial://callback',
        code: authorizationCode,
        code_verifier: codeVerifier,
      });

      return token;
    } catch (e) {
      if (e instanceof ApiError) {
        return rejectWithValue(e);
      }

      throw e;
    }
  },
);

export const logout = createAsyncThunk<void, boolean | undefined>(
  'auth/logout',
  async (sendLogoutRequest = true) => {
    // If we are under maintenance the logout request would also fail
    if (sendLogoutRequest) {
      await AuthService.logout();
    }

    await SecureStorage.removeItem('token');

    await SecureStorage.removeItem('access_token');
    await SecureStorage.removeItem('user_id');
    await SecureStorage.removeItem('refresh_token');
  },
);

export const resendMFACode = createAsyncThunk<
  ResendCodeResult,
  ResendCodePayload
>('auth/resendMFACode', async (data) => {
  return await AuthService.resendMFACode({ data });
});

export const resetPasswordLink = createAsyncThunk(
  'auth/resetPasswordLink',
  async (email: string) => {
    await AuthService.resetPasswordLink({ data: { email } });
  },
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({
    token,
    accountId,
    newPassword,
    newPasswordConfirmation,
  }: {
    token: string;
    accountId: string;
    newPassword: string;
    newPasswordConfirmation: string;
  }) => {
    await AuthService.resetPassword({
      data: { token, accountId, newPassword, newPasswordConfirmation },
    });
  },
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async ({
    accountId,
    currentPassword,
    newPassword,
  }: {
    accountId: string;
    currentPassword: string;
    newPassword: string;
  }) => {
    await AuthService.changePassword({
      data: {
        accountId,
        currentPassword,
        newPassword,
      },
    });
  },
);

const initialState: AuthState = {
  isLoggedIn: false,
  isExistingUser: false,
  existingUserName: undefined,
  loginDataState: 'idle',
  logoutDataState: 'idle',
  validateMFADataState: 'idle',
  changePasswordDataState: 'idle',
  resendMFADataState: 'idle',
  resetPasswordDataState: 'idle',
  refreshTokenDataState: 'idle',
};

const auth = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setRefreshTokenDataState(
      state,
      action: PayloadAction<AuthState['refreshTokenDataState']>,
    ) {
      state.refreshTokenDataState = action.payload;
    },
    setIsExistingUser(state, action: PayloadAction<boolean>) {
      state.isExistingUser = action.payload;
    },
    setExistingUserName(state, action: PayloadAction<string>) {
      state.existingUserName = action.payload;
    },
    resetMFAResendCodeTimeout(state) {
      state.mfaResendCodeTimeout = +new Date() + 60000; // Add 1 minute
    },
    clearMFAResendCodeTimeout(state) {
      state.mfaResendCodeTimeout = undefined;
    },
    setLoggedIn(state, action: PayloadAction<boolean>) {
      state.isLoggedIn = action.payload;
      if (action.payload) {
        state.loginDataState = 'fulfilled';
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(login.pending, (state) => {
      state.loginDataState = 'pending';
    });
    builder.addCase(login.fulfilled, (state) => {
      state.isLoggedIn = true;
      state.loginDataState = 'fulfilled';
    });
    builder.addCase(login.rejected, (state) => {
      state.loginDataState = 'rejected';
    });
    builder.addCase(validateMFA.pending, (state) => {
      state.validateMFADataState = 'pending';
    });
    builder.addCase(validateMFA.fulfilled, (state) => {
      state.isLoggedIn = true;
      state.validateMFADataState = 'fulfilled';
    });
    builder.addCase(validateMFA.rejected, (state) => {
      state.validateMFADataState = 'rejected';
    });
    builder.addCase(changePassword.pending, (state) => {
      state.changePasswordDataState = 'pending';
    });
    builder.addCase(changePassword.fulfilled, (state) => {
      state.changePasswordDataState = 'fulfilled';
    });
    builder.addCase(changePassword.rejected, (state) => {
      state.changePasswordDataState = 'rejected';
    });
    builder.addCase(logout.pending, (state) => {
      state.logoutDataState = 'pending';
    });
    builder.addCase(logout.fulfilled, (state) => {
      const { isExistingUser, existingUserName } = state;
      return {
        ...initialState,
        isExistingUser,
        existingUserName,
      };
    });
    builder.addCase(logout.rejected, (state) => {
      state.logoutDataState = 'rejected';
    });
    builder.addCase(resetPassword.pending, (state) => {
      state.resetPasswordDataState = 'pending';
    });
    builder.addCase(resetPassword.fulfilled, (state) => {
      state.resetPasswordDataState = 'fulfilled';
    });
    builder.addCase(resetPassword.rejected, (state) => {
      state.resetPasswordDataState = 'rejected';
    });
    builder.addCase(refreshToken.fulfilled, (state) => {
      state.refreshTokenDataState = 'fulfilled';
    });
    builder.addCase(refreshToken.rejected, (state) => {
      state.isLoggedIn = false;
      state.refreshTokenDataState = 'rejected';
    });
    builder.addCase(resendMFACode.pending, (state) => {
      state.resendMFADataState = 'pending';
    });
    builder.addCase(REHYDRATE, (state, action: AuthStateRehydration) => {
      const { key, payload } = action;

      if (key === 'auth' && payload?.isLoggedIn) {
        state.refreshTokenDataState = 'stale';
      }
    });
    builder.addMatcher(resendMFACode.settled, (state) => {
      state.resendMFADataState = 'idle';
    });
  },
});

export const {
  resetMFAResendCodeTimeout,
  clearMFAResendCodeTimeout,
  setRefreshTokenDataState,
  setLoggedIn,
  setIsExistingUser,
  setExistingUserName,
} = auth.actions;

const persistConfig = {
  key: 'auth',
  storage: AsyncStorage,
  whitelist: [
    'isLoggedIn',
    'refreshTokenDataState',
    'isExistingUser',
    'existingUserName',
  ],
};

const persistedReducer = persistReducer(persistConfig, auth.reducer);

const withAuth = injectSlice({
  reducerPath: 'auth',
  reducer: persistedReducer,
});
export const selectIsLogged = withAuth.selector(
  (state) => state.auth.isLoggedIn,
);

export const selectIsExistingUser = withAuth.selector(
  (state) => state.auth.isExistingUser,
);

export const selectExistingUserName = withAuth.selector(
  (state) => state.auth.existingUserName,
);
export const selectLoginDataState = withAuth.selector(
  (state) => state.auth.loginDataState,
);
export const selectValidateMFADataState = withAuth.selector(
  (state) => state.auth.validateMFADataState,
);

export const selectMFAResendCodeDataState = withAuth.selector(
  (state) => state.auth.resendMFADataState,
);

export const selectMFAResendCodeTimeout = withAuth.selector(
  (state) => state.auth.mfaResendCodeTimeout,
);
export const selectRefreshTokenDataState = withAuth.selector(
  (state) => state.auth.refreshTokenDataState,
);
export const selectChangePasswordDataState = withAuth.selector(
  (state) => state.auth?.changePasswordDataState,
);

export const selectResetPasswordDataState = withAuth.selector(
  (state) => state.auth?.resetPasswordDataState,
);

export const getRefreshTokenPromise = () => refreshTokenPromise;

declare global {
  interface LazyLoadedSlices {
    auth: ReturnType<typeof persistedReducer>;
  }
}
